import { useMemo } from 'react'
import { useUniswapContextSelector } from 'uniswap/src/contexts/UniswapContext'
import { ALL_NETWORKS_ARG } from 'uniswap/src/data/rest/base'
import { tokenRankingsStatToCurrencyInfo, useTokenRankingsQuery } from 'uniswap/src/data/rest/tokenRankings'
import { CustomRankingType } from 'uniswap/src/data/types'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import { CurrencyInfo } from 'uniswap/src/features/dataApi/types'
import { getPreferredTokenLogoUrl } from 'uniswap/src/features/tokens/getPreferredTokenLogoUrl'

export function useTrendingTokensCurrencyInfos(
  chainFilter: Maybe<UniverseChainId>,
  skip?: boolean,
): {
  data: CurrencyInfo[] | undefined
  error: Error | undefined
  refetch: () => void
  loading: boolean
} {
  const searchLocalTokenSelectorResults = useUniswapContextSelector((ctx) => ctx.searchLocalTokenSelectorResults)
  const { data, isLoading, error, refetch, isFetching } = useTokenRankingsQuery(
    {
      chainId: chainFilter?.toString() ?? ALL_NETWORKS_ARG,
    },
    !skip,
  )

  const trendingTokens = data?.tokenRankings?.[CustomRankingType.Trending]?.tokens
  const formattedTokens = useMemo(() => {
    const tokens = trendingTokens
      ?.map(tokenRankingsStatToCurrencyInfo)
      .filter((t): t is CurrencyInfo => Boolean(t))
      .map((currencyInfo) => {
        const { currency } = currencyInfo
        if (!currency.isToken) {
          return currencyInfo
        }

        const localCurrencyInfo = searchLocalTokenSelectorResults?.({
          chainFilter: currency.chainId as UniverseChainId,
          searchFilter: currency.address,
        })?.find(
          (localInfo) =>
            localInfo.currency.chainId === currency.chainId &&
            localInfo.currency.isToken &&
            localInfo.currency.address.toLowerCase() === currency.address.toLowerCase(),
        )

        const logoUrl = getPreferredTokenLogoUrl(currencyInfo, localCurrencyInfo?.logoUrl)
        return logoUrl === currencyInfo.logoUrl ? currencyInfo : { ...currencyInfo, logoUrl }
      })
    // Filter by chainFilter if specified, to ensure only tokens from the selected chain are shown
    if (chainFilter && tokens) {
      return tokens.filter((token) => token.currency.chainId === chainFilter)
    }
    return tokens
  }, [chainFilter, searchLocalTokenSelectorResults, trendingTokens])

  return { data: formattedTokens, loading: isLoading || isFetching, error: error ?? undefined, refetch }
}
